import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Admin (AuthContext) and customer (UserContext) sessions share this one axios instance but
// use separate tokens ('token' vs 'userToken'). Routes under these prefixes are only ever
// called with a customer's identity, so they must always use the customer token — otherwise,
// on a browser where both an admin and a customer are logged in, the admin token silently
// wins on every request and calls like /user/me or /user/bookings resolve to the wrong account.
const USER_SCOPED_URL_MATCHERS = [
  (url) => url.startsWith('/user/'),
  (url) => url.startsWith('/booking/request'),
  (url) => url.includes('/tickets/my-tickets'),
];
const isUserScopedUrl = (url = '') => USER_SCOPED_URL_MATCHERS.some((match) => match(url));

// Request interceptor for adding auth token and correct Content-Type
api.interceptors.request.use(
  (config) => {
    const adminToken = localStorage.getItem('token');
    const userToken = localStorage.getItem('userToken');
    const token = isUserScopedUrl(config.url) ? (userToken || adminToken) : (adminToken || userToken);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Only set JSON content-type if we're NOT sending FormData.
    // For FormData, the browser MUST set Content-Type itself (with multipart boundary).
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (isUserScopedUrl(error.config?.url)) {
        localStorage.removeItem('userToken');
        delete api.defaults.headers.common['Authorization'];
      } else {
        localStorage.removeItem('token');
        if (window.location.pathname.startsWith('/admin') ||
          window.location.pathname.startsWith('/superadmin')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// API functions for different modules

// Stations
export const stationsAPI = {
  getAll: (params) => api.get('/stations', { params }),
  getOne: (id) => api.get(`/stations/${id}`),
  create: (data) => api.post('/stations', data),
  update: (id, data) => api.put(`/stations/${id}`, data),
  delete: (id) => api.delete(`/stations/${id}`),
};

// Categories
export const categoriesAPI = {
  getAll: (params) => api.get('/categories', { params }),
  getOne: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Rooms
export const roomsAPI = {
  getAll: (params) => api.get('/rooms', { params }),
  getOne: (id) => api.get(`/rooms/${id}`),
  create: (data) => api.post('/rooms', data),
  update: (id, data) => api.put(`/rooms/${id}`, data),
  updateStatus: (id, status) => api.patch(`/rooms/${id}/status`, { status }),
  delete: (id) => api.delete(`/rooms/${id}`),
};

// Bookings
export const bookingsAPI = {
  createRequest: (data) => {
    const isFormData = data instanceof FormData;
    return api.post('/booking/request', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined
    });
  },
  adminCreate: (data) => {
    const isFormData = data instanceof FormData;
    return api.post('/booking/admin-create', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined
    });
  },
  getStatus: (id) => api.get(`/booking/status/${id}`),
  // ID documents are never public URLs — they are streamed from an authenticated route and
  // rendered from an object URL, so they cannot be hot-linked or cached by intermediaries.
  getIdCard: (bookingId) => api.get(`/booking/${bookingId}/id-card`, { responseType: 'blob' }),
  getRequests: (params) => api.get('/booking/requests', { params }),
  getAvailability: (stationId, date, categoryId) => api.get(`/booking/availability/${stationId}`, { params: { date, categoryId } }),
  getAdvanceBookings: (params) => api.get('/booking/advance', { params }),
  allocate: (data) => api.post('/booking/allocate', data),
  reject: (data) => api.post('/booking/reject', data),
  checkIn: (bookingId, roomId) => api.post('/booking/checkin', { bookingId, roomId }),
  // Adjustments are passed as an object: `extraCharges` only ever adds to the bill and
  // `discount` only ever subtracts. Both must be non-negative — the server rejects
  // anything else rather than letting a negative charge stand in for a discount.
  checkOut: (bookingId, adjustments = {}) =>
    api.post('/booking/checkout', {
      bookingId,
      extraCharges: adjustments.extraCharges ?? 0,
      extraChargesNote: adjustments.extraChargesNote ?? '',
      discount: adjustments.discount ?? 0,
      discountNote: adjustments.discountNote ?? ''
    }),
  updateBill: (bookingId, data) => api.put(`/booking/${bookingId}/bill`, data),
};

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  googleLogin: (credential) => api.post('/auth/google', { credential }),
  getMe: () => api.get('/auth/me'),
};

// Admin management
export const adminsAPI = {
  getAll: () => api.get('/admin/admins'),
  getOne: (id) => api.get(`/admin/admins/${id}`),
  create: (data) => api.post('/admin/admins', data),
  update: (id, data) => api.put(`/admin/admins/${id}`, data),
  resetPassword: (id, newPassword) => api.put(`/admin/admins/${id}/password`, { newPassword }),
  delete: (id) => api.delete(`/admin/admins/${id}`),
  getDashboard: (params) => api.get('/admin/dashboard', { params }),
  // Reads the on-disk JSON audit trail. Accepts { limit, station, admin, action, outcome,
  // targetId, since, until }.
  getActivityLogs: (params) => api.get('/admin/activity-logs', { params }),
  getAuditLogFiles: () => api.get('/admin/audit-log-files'),
};

// Settings
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  getGST: () => api.get('/settings/gst'),
  updateGST: (data) => api.put('/settings/gst/update', data),
  update: (key, data) => api.put(`/settings/${key}`, data),
};

// Payment
export const paymentAPI = {
  initiate: (bookingId) => api.post('/payment/initiate', { bookingId }),
  checkStatus: (merchantOrderId) => api.get(`/payment/status/${merchantOrderId}`),
};

// User (logged-in traveller)
export const userAPI = {
  getMyBookings: () => api.get('/user/bookings'),
};

